import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorBoundaryState, ErrorDetails } from '@/shared/types/common.types';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: ErrorDetails) => ReactNode;
  onError?: (error: ErrorDetails) => void;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorDetails: ErrorDetails = {
      code: 'REACT_ERROR_BOUNDARY',
      message: error.message,
      timestamp: Date.now(),
      stack: error.stack,
      context: {
        componentStack: errorInfo.componentStack,
      },
    };

    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }

    // Call the onError callback if provided
    this.props.onError?.(errorDetails);

    this.setState({
      hasError: true,
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      const errorDetails: ErrorDetails = {
        code: 'REACT_ERROR_BOUNDARY',
        message: this.state.error?.message || 'An unexpected error occurred',
        timestamp: Date.now(),
        stack: this.state.error?.stack,
        context: {
          componentStack: this.state.errorInfo?.componentStack,
        },
      };

      if (this.props.fallback) {
        return this.props.fallback(errorDetails);
      }

      return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 text-error-500">
                <svg
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              
              <h1 className="text-xl font-semibold text-neutral-900 mb-2">
                Something went wrong
              </h1>
              
              <p className="text-neutral-600 mb-6">
                We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <details className="text-left mb-6 p-4 bg-neutral-100 rounded-lg">
                  <summary className="cursor-pointer font-medium mb-2">
                    Error Details (Development)
                  </summary>
                  <pre className="text-xs text-neutral-700 whitespace-pre-wrap overflow-auto">
                    {this.state.error.message}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                    {this.state.errorInfo?.componentStack && 
                      `\n\nComponent Stack:${this.state.errorInfo.componentStack}`
                    }
                  </pre>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={this.handleReset} variant="primary">
                  Try Again
                </Button>
                <Button 
                  onClick={() => window.location.reload()} 
                  variant="secondary"
                >
                  Reload Page
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-neutral-200">
                <p className="text-sm text-neutral-500">
                  If this problem continues, please{' '}
                  <a 
                    href="mailto:support@civista.co.uk" 
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    contact our support team
                  </a>
                  .
                </p>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}